import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { toast } from 'sonner';
import { toastRequestError } from '@/api/mutations/toast-api-error';
import { Save, Settings } from 'lucide-react';

import { DEFAULT_REVIEW_TIMEFRAME } from '@/constants';

import {
  DEFAULT_BILLING_RATE,
  DEFAULT_LANGUAGE,
  DEFAULT_CAPACITY_PRIORITY,
  type CapacityPriority,
  type PrivacyMode,
  type SupportedLanguage
} from '@bliks-io/contracts';
import { Button, Page, PageContent, PageFooter, BottomBar, BottomBarSection } from '@bliks-io/ui';
import { useOrganization, useUpdateOrganization } from '@/api/queries/useOrganization';
import { useOrgLogoEditor } from '@/hooks/useOrgLogoEditor';

import { ContentHeader } from '@/components/content-header';
import { BackendDataExportButton } from './components/backendDataExportButton';
import { OrganizationProfileCard } from '@/components/organization/organizationProfileCard';
import { PreferencesCard } from './components/preferencesCard';
import { SurveySettingsCard } from './components/surveySettingsCard';
import { AiSettingsCard } from './components/aiSettingsCard';
import { SettingsPageSkeleton } from './components/SettingsPageSkeleton';

interface FormState {
  id: string;
  name: string;
  defaultLanguage: SupportedLanguage;
  recordingTime: number;
  reviewTimeframe: number;
  internalBillingRate: number;
  enableRatingAndComment: boolean;
  enableFrequencyMeasurement: boolean;
  enableDurationMeasurement: boolean;
  enableVariantCheck: boolean;
  bliksyEnabled: boolean;
  stepNameGenerationEnabled: boolean;
  privacyMode: PrivacyMode;
  capacityPriority: CapacityPriority;
}

export function SettingsPage() {
  const { t } = useTranslation();

  useDocumentTitle('pageTitles.settings');

  const { data: org, isLoading: loadingOrg } = useOrganization();
  const updateOrg = useUpdateOrganization();
  const logoEditor = useOrgLogoEditor(org?.logo);
  const [isSaving, setIsSaving] = useState(false);

  // Read-only: controlled by superadmin
  const aiEnabled = org?.settings?.enableAI || false;

  const [form, setForm] = useState<FormState>({
    id: '',
    name: '',
    defaultLanguage: DEFAULT_LANGUAGE,
    recordingTime: 48,
    reviewTimeframe: DEFAULT_REVIEW_TIMEFRAME,
    internalBillingRate: DEFAULT_BILLING_RATE,
    enableRatingAndComment: false,
    enableFrequencyMeasurement: false,
    enableDurationMeasurement: false,
    enableVariantCheck: false,
    bliksyEnabled: false,
    stepNameGenerationEnabled: false,
    privacyMode: 'need-to-know',
    capacityPriority: DEFAULT_CAPACITY_PRIORITY
  });

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Logo first: it is its own upload → confirm (or delete) round-trip, and
      // the backend writes the organization itself as part of confirming.
      await logoEditor.save();

      // Update organization settings (enableAI is not sent — superadmin-only)
      await updateOrg.mutateAsync({
        settings: {
          defaultLanguage: form.defaultLanguage,
          enableRecordingSurvey: form.enableRatingAndComment,
          enableFrequencySurvey: form.enableFrequencyMeasurement,
          enableDurationSurvey: form.enableDurationMeasurement,
          enableVariantCheck: form.enableVariantCheck,
          standardRecordingTime: form.recordingTime,
          reviewTimeframe: form.reviewTimeframe,
          internalBillingRate: form.internalBillingRate,
          ai: {
            enableBliksy: form.bliksyEnabled,
            enableStepNameGeneration: form.stepNameGenerationEnabled
          },
          privacyMode: form.privacyMode,
          capacityPriority: form.capacityPriority
        }
      });

      // TanStack Query automatically refetches organization data via cache invalidation
      // Any component using useOrganization() will get the updated data

      toast.success(t('OrgSettingPage.SettingSaved'));
    } catch (error) {
      // A logo failure also lands here; useOrgLogoEditor keeps the API message
      // for the card, so the toast only has to say that saving failed.
      toastRequestError(error, t('common.Error'));
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(
    function syncFormWithOrganization() {
      if (org) {
        setForm((prev) => ({
          ...prev,
          id: org._id as string,
          name: org.name || '',
          defaultLanguage: org.settings?.defaultLanguage || DEFAULT_LANGUAGE,
          enableRatingAndComment: org.settings?.enableRecordingSurvey || false,
          enableFrequencyMeasurement: org.settings?.enableFrequencySurvey || false,
          enableDurationMeasurement: org.settings?.enableDurationSurvey || false,
          enableVariantCheck: org.settings?.enableVariantCheck || false,
          bliksyEnabled: org.settings?.ai?.enableBliksy || false,
          stepNameGenerationEnabled: org.settings?.ai?.enableStepNameGeneration || false,
          recordingTime: org.settings?.standardRecordingTime || 48,
          reviewTimeframe: org.settings?.reviewTimeframe || DEFAULT_REVIEW_TIMEFRAME,
          internalBillingRate: org.settings?.internalBillingRate ?? DEFAULT_BILLING_RATE,
          privacyMode: org.settings?.privacyMode || 'need-to-know',
          capacityPriority: org.settings?.capacityPriority || DEFAULT_CAPACITY_PRIORITY
        }));
      }
    },
    [org]
  );

  const validate = useMemo(() => {
    const isMissingReviewTimeFrame = !form.reviewTimeframe ? t('OrgSettingPage.fieldRequired') : '';
    const isFloatReviewTimeframe = `${form.reviewTimeframe}`.includes(',')
      ? t('OrgSettingPage.noCommaAllowed')
      : '';

    const isMissingRecordingTime = !form.recordingTime ? t('OrgSettingPage.fieldRequired') : '';
    const isFloatRecordingTime = `${form.recordingTime}`.includes(',')
      ? t('OrgSettingPage.noCommaAllowed')
      : '';

    // Billing rate must be positive. `!(x > 0)` (not `x <= 0`) also rejects the NaN/0 a blank input coerces to.
    const isNonPositiveBillingRate = !(form.internalBillingRate > 0)
      ? t('OrgSettingPage.mustBePositive')
      : '';

    return {
      recordingTime: isMissingRecordingTime || isFloatRecordingTime,
      reviewTimeframe: isMissingReviewTimeFrame || isFloatReviewTimeframe,
      internalBillingRate: isNonPositiveBillingRate
    };
  }, [form.recordingTime, form.reviewTimeframe, form.internalBillingRate, t]);

  // Show loading state while fetching organization data
  if (loadingOrg) {
    return <SettingsPageSkeleton />;
  }

  return (
    <Page>
      <PageContent>
        <ContentHeader
          icon={Settings}
          title={t('OrgSettingPage.Settings')}
          description={t('OrgSettingPage.editKeyDetails')}
        />

        <div className="flex flex-col gap-6 mt-8">
          {/* Organization Profile Card */}
          <OrganizationProfileCard
            name={form.name}
            logo={logoEditor.logo}
            file={logoEditor.file}
            error={logoEditor.error}
            disabled={isSaving}
            onRemove={logoEditor.handleRemove}
            onFileChange={logoEditor.handleFileChange}
          />

          {/* Preferences and Survey Settings - Side by Side */}
          <div className="flex flex-col lg:flex-row lg:items-stretch gap-6">
            {/* Preferences Card */}
            <div className="flex-1">
              <PreferencesCard
                defaultLanguage={form.defaultLanguage}
                recordingTime={form.recordingTime}
                reviewTimeframe={form.reviewTimeframe}
                internalBillingRate={form.internalBillingRate}
                privacyMode={form.privacyMode}
                capacityPriority={form.capacityPriority}
                onLanguageChange={(v) => setForm((prev) => ({ ...prev, defaultLanguage: v }))}
                onRecordingTimeChange={(v) => setForm((prev) => ({ ...prev, recordingTime: v }))}
                onReviewTimeframeChange={(v) =>
                  setForm((prev) => ({ ...prev, reviewTimeframe: v }))
                }
                onInternalBillingRateChange={(v) =>
                  setForm((prev) => ({ ...prev, internalBillingRate: v }))
                }
                onPrivacyModeChange={(v) => setForm((prev) => ({ ...prev, privacyMode: v }))}
                onCapacityPriorityChange={(v) =>
                  setForm((prev) => ({ ...prev, capacityPriority: v }))
                }
                recordingTimeError={validate.recordingTime}
                reviewTimeframeError={validate.reviewTimeframe}
                internalBillingRateError={validate.internalBillingRate}
                className="h-full"
              />
            </div>

            {/* Survey Settings Card */}
            <div className="flex-1">
              <SurveySettingsCard
                enableRatingAndComment={form.enableRatingAndComment}
                enableFrequencyMeasurement={form.enableFrequencyMeasurement}
                enableDurationMeasurement={form.enableDurationMeasurement}
                enableVariantCheck={form.enableVariantCheck}
                onToggleRatingAndComment={(v) =>
                  setForm((prev) => ({ ...prev, enableRatingAndComment: v }))
                }
                onToggleFrequencyMeasurement={(v) =>
                  setForm((prev) => ({ ...prev, enableFrequencyMeasurement: v }))
                }
                onToggleDurationMeasurement={(v) =>
                  setForm((prev) => ({ ...prev, enableDurationMeasurement: v }))
                }
                onToggleVariantCheck={(v) =>
                  setForm((prev) => ({ ...prev, enableVariantCheck: v }))
                }
                className="h-full"
              />
            </div>
          </div>

          {/* AI Settings Card — only visible when AI is enabled by superadmin */}
          {aiEnabled && (
            <AiSettingsCard
              bliksyEnabled={form.bliksyEnabled}
              onToggleBliksyEnabled={(v) => setForm((prev) => ({ ...prev, bliksyEnabled: v }))}
              stepNameGenerationEnabled={form.stepNameGenerationEnabled}
              onToggleStepNameGeneration={(v) =>
                setForm((prev) => ({ ...prev, stepNameGenerationEnabled: v }))
              }
            />
          )}
        </div>
      </PageContent>
      <PageFooter>
        <BottomBar>
          <BottomBarSection>
            <BackendDataExportButton />
          </BottomBarSection>
          <BottomBarSection>
            <Button loading={isSaving} onClick={handleSave}>
              <Save className="w-4 h-4 mr-2" />
              {t('common.Save')}
            </Button>
          </BottomBarSection>
        </BottomBar>
      </PageFooter>
    </Page>
  );
}
